<script>
import baseMixin from '@/mixins/baseMixin'
import { mapActions, mapGetters } from 'vuex'
import HttpStatusCodes from '@/constants/api/HttpStatusCodes'
import EmptyStateBlock from '../general/empty-state-block.vue'
import FormSubmissionSkeleton from '../loaders/form-submission-skeleton.vue'
import FormSubmissionDetails from './form-submission-details.vue'

export default {
  name: 'FormSubmissionContainer',
  components: {
    EmptyStateBlock,
    FormSubmissionSkeleton,
    FormSubmissionDetails,
  },
  mixins: [baseMixin],
  data: () => {
    return {
      failedToLoad: false,
      errorObj: null,
      form: null,
    }
  },
  computed: {
    ...mapGetters({
      isLoadingFormSubmission: 'forms/isLoadingFormSubmission',
    }),
    compErrorCode() {
      return this.errorObj.code
    },
    compErrorMessage() {
      switch (this.errorObj.status) {
        case HttpStatusCodes.NotFound:
          return `Unable to find form submission.`
        case HttpStatusCodes.Unauthorized:
        case HttpStatusCodes.Forbidden:
          return 'Could not authenticate with the Forms server.'
        default:
          return 'There was an issue trying to load this form submission.'
      }
    },
    hasLoadedSchemaAndSubmission() {
      return !!this.form
    },
  },
  async created() {
    await this.loadForm()
  },
  methods: {
    ...mapActions({
      loadFormSubmission: 'forms/loadFormSubmission',
    }),
    resetError() {
      this.failedToLoad = false
      this.errorObj = null
    },
    async loadForm() {
      this.resetError()

      const { isSuccess, data, errors } = await this.loadFormSubmission(
        this.$route.params.passkey
      )

      if (!isSuccess) {
        this.failedToLoad = true
        this.errorObj = {
          code: errors?.data?.code || 'unknown_error',
          status: errors?.status,
        } || {
          code: 'unknown_error',
          status: null,
        }
        return
      }

      this.form = data
    },
  },
}
</script>

<template>
  <div>
    <v-row class="d-flex align-center justify-center">
      <v-col cols="12" lg="6" md="12">
        <div v-if="isLoadingFormSubmission">
          <FormSubmissionSkeleton />
        </div>
        <div v-else-if="failedToLoad">
          <EmptyStateBlock :title="'Uh oh!'" border>
            <template v-slot:description>
              <p class="mb-0">{{ compErrorMessage }}</p>
              <p class="caption">
                Error code: <code>{{ compErrorCode }}</code></p
              >
            </template>
            <template v-slot:button>
              <div class="mt-4">
                <v-btn
                  data-testid="failure-to-load-retry-btn"
                  color="primary"
                  class="white--text"
                  depressed
                  large
                  @click="loadForm"
                >
                  <v-icon left> mdi-refresh </v-icon>
                  {{ $t('general.retry') }}
                </v-btn>
              </div>
            </template>
          </EmptyStateBlock>
        </div>
        <div v-else-if="hasLoadedSchemaAndSubmission">
          <FormSubmissionDetails :form="form" />
        </div>
      </v-col>
    </v-row>
  </div>
</template>
